import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, BookIcon, LibraryIcon, SlidersHorizontalIcon, ChevronDownIcon, FilterIcon, PencilIcon, XIcon } from 'lucide-react';
import { tabs } from './constants';
interface BookshelfToolbarProps {
  darkMode: boolean;
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  selectedGenre: string;
  setSelectedGenre: (val: string) => void;
  genres: string[];
  selectedSubgenre: string;
  setSelectedSubgenre: (val: string) => void;
  subgenres: string[];
  selectedRelationship: string;
  setSelectedRelationship: (val: string) => void;
  relationshipTypes: string[];
  viewMode: 'spine' | 'cover';
  setViewMode: (val: 'spine' | 'cover') => void;
  activeTab: string;
  showFilters: boolean;
  setShowFilters: (val: boolean) => void;
  sortOption: string;
  setSortOption: (val: string) => void;
  seriesFilter: string;
  setSeriesFilter: (val: string) => void;
  selectedFormat: string;
  setSelectedFormat: (val: string) => void;
  formats: string[];
  isEditMode: boolean;
  toggleEditMode: () => void;
  selectedBooks: string[];
  clearSelection: () => void;
  selectAllBooks: () => void;
  bulkChangeStatus: (status: string) => void;
  bulkDelete: () => void;
}
export const BookshelfToolbar: React.FC<BookshelfToolbarProps> = ({
  darkMode,
  searchTerm,
  setSearchTerm,
  selectedGenre,
  setSelectedGenre,
  genres,
  selectedSubgenre,
  setSelectedSubgenre,
  subgenres,
  selectedRelationship,
  setSelectedRelationship,
  relationshipTypes,
  viewMode,
  setViewMode,
  activeTab,
  showFilters,
  setShowFilters,
  sortOption,
  setSortOption,
  seriesFilter,
  setSeriesFilter,
  selectedFormat,
  setSelectedFormat,
  formats,
  isEditMode,
  toggleEditMode,
  selectedBooks,
  clearSelection,
  selectAllBooks,
  bulkChangeStatus,
  bulkDelete
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const viewOptionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (viewOptionsRef.current && !viewOptionsRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters, setShowFilters]);
  const activeFilterCount = [selectedGenre, selectedSubgenre, selectedRelationship].filter(Boolean).length;
  return <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 md:p-4 mb-6`} data-id="element-10071" data-mp="wxkp32hfc7d3">
      <div className="flex flex-col space-y-3" data-id="element-10072" data-mp="iyb65v1u2z5f">
        {/* Row 1: Search + mobile icon controls */}
        <div className="flex items-center gap-2" data-id="element-10073" data-mp="nxnukk8csea4">
          <div className="relative flex-1" data-id="element-10074" data-mp="nyh57wafnnxj">
            <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-10075" data-mp="2dbarm4swankm" />
            <input type="text" placeholder="Search books..." className={`pl-9 pr-4 py-2 w-full rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border border-gray-300 text-gray-900'}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10076" data-mp="2selqo1897v92" />
          </div>

          {/* Mobile-only compact controls */}
          <div className="flex items-center gap-1 md:hidden" data-id="element-10077" data-mp="365z736av8czv">
            {/* Filter toggle */}
            <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)} className={`relative p-2 rounded-md ${mobileFiltersOpen ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} aria-label="Filters" data-id="element-10078" data-mp="3k5rtwqj9u92k">
              <FilterIcon className="h-4 w-4" data-id="element-10079" data-mp="1lqf7e5oerawx" />
              {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-indigo-500 text-white text-[10px] flex items-center justify-center font-bold" data-id="element-10080" data-mp="1n7ktu5qhs9pm">
                  {activeFilterCount}
                </span>}
            </button>
            {/* View toggle */}
            <button onClick={() => setViewMode(viewMode === 'cover' ? 'spine' : 'cover')} className={`p-2 rounded-md ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} aria-label={viewMode === 'cover' ? 'Switch to spine view' : 'Switch to cover view'} data-id="element-10081" data-mp="2173p3xy7fqki">
              {viewMode === 'cover' ? <LibraryIcon className="h-4 w-4" data-id="element-10082" data-mp="1i9j1eu76ey1h" /> : <BookIcon className="h-4 w-4" data-id="element-10083" data-mp="krsrn4wibpel" />}
            </button>
            {/* Edit mode */}
            <button onClick={toggleEditMode} className={`p-2 rounded-md ${isEditMode ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} aria-label={isEditMode ? 'Exit edit mode' : 'Edit books'} data-id="element-10084" data-mp="pqcc10vgct4u">
              {isEditMode ? <XIcon className="h-4 w-4" data-id="element-10085" data-mp="8c2sye52f6av" /> : <PencilIcon className="h-4 w-4" data-id="element-10086" data-mp="483dlyviudj3" />}
            </button>
          </div>
        </div>

        {/* Mobile filter panel */}
        {mobileFiltersOpen && <div className="md:hidden space-y-3 pt-1" data-id="element-10087" data-mp="3qn67z6ql670y">
            <div className="grid grid-cols-1 gap-3" data-id="element-10088" data-mp="3lp4gpyj0b1ee">
              <div data-id="element-10089" data-mp="3lpw9z2p4sn1i">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10090" data-mp="3ln1snk1ezicb">
                  Genre
                </label>
                <select value={selectedGenre} onChange={e => setSelectedGenre(e.target.value)} className={`w-full text-sm rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10091" data-mp="3cqj1ac8u02hu">
                  <option value="" data-id="element-10092" data-mp="2o3jsv1zkv423">All Genres</option>
                  {genres.map(genre => <option key={genre} value={genre} data-id="element-10093" data-mp="2t2msg6wsgi2o">
                      {genre}
                    </option>)}
                </select>
              </div>
              <div data-id="element-10094" data-mp="3gubs68bxw8tn">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10095" data-mp="3lrbx2fx1z5j6">
                  Subgenre
                </label>
                <select value={selectedSubgenre} onChange={e => setSelectedSubgenre(e.target.value)} className={`w-full text-sm rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10096" data-mp="3c18xfwvfetbv">
                  <option value="" data-id="element-10097" data-mp="1t1sxfukocf42">All Subgenres</option>
                  {subgenres.map(s => <option key={s} value={s} data-id="element-10098" data-mp="1o2okqtyjjz8z">
                      {s}
                    </option>)}
                </select>
              </div>
              <div data-id="element-10099" data-mp="1xtt383nfe28k">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10100" data-mp="1xtax8fayo9ng">
                  Relationship Type
                </label>
                <select value={selectedRelationship} onChange={e => setSelectedRelationship(e.target.value)} className={`w-full text-sm rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10101" data-mp="1566zmve76xlp">
                  <option value="" data-id="element-10102" data-mp="2ulmv57z78kt4">All Relationships</option>
                  {relationshipTypes.map(type => <option key={type} value={type} data-id="element-10103" data-mp="39mtmschgabcj">
                      {type}
                    </option>)}
                </select>
              </div>
              {activeTab === 'ownedShelf' && <>
                  <div data-id="element-10104" data-mp="3imq947gday4f">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10105" data-mp="3ilf464b6rkbq">
                      Sort by
                    </label>
                    <select value={sortOption} onChange={e => setSortOption(e.target.value)} className={`w-full text-sm rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10106" data-mp="171mw3b92juq">
                      <option value="seriesOrder" data-id="element-10107" data-mp="27yj74f2e3gff">Series Order</option>
                      <option value="alphabetical" data-id="element-10108" data-mp="2bytb5m6mfwrn">Alphabetical</option>
                      <option value="dateOwned" data-id="element-10109" data-mp="2c0wd94l1tgcq">Date Owned</option>
                    </select>
                  </div>
                  <div data-id="element-10110" data-mp="1zotw0els1t7d">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10111" data-mp="1zolj4mpnmnw6">
                      View Mode
                    </label>
                    <select value={seriesFilter} onChange={e => setSeriesFilter(e.target.value)} className={`w-full text-sm rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10112" data-mp="1qpqevg667nvi">
                      <option value="all" data-id="element-10113" data-mp="29en95k1kigdq">All Series</option>
                      <option value="allBooks" data-id="element-10114" data-mp="29e57u901tbyy">All Books Owned</option>
                      <option value="completed" data-id="element-10115" data-mp="12zmbn9ww9q51">Completed Series</option>
                      <option value="ongoing" data-id="element-10116" data-mp="xzrj71xh0shv">Ongoing Series</option>
                      <option value="standalone" data-id="element-10117" data-mp="y1kuwvmxy2wq">Standalone Books Only</option>
                    </select>
                  </div>
                  <div data-id="element-10118" data-mp="2wzlfpvvxq4a">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10119" data-mp="2xsiu08yf39r">
                      Format
                    </label>
                    <select value={selectedFormat} onChange={e => setSelectedFormat(e.target.value)} className={`w-full text-sm rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10120" data-mp="bvurn1yxj07l">
                      <option value="" data-id="element-10121" data-mp="vlocsx752h2u">All Formats</option>
                      {formats.map(format => <option key={format} value={format} data-id="element-10122" data-mp="31dtrcglw4vvp">
                          {format.charAt(0).toUpperCase() + format.slice(1)}
                        </option>)}
                    </select>
                  </div>
                </>}
            </div>
          </div>}

        {/* Desktop filter dropdowns */}
        <div className="hidden md:grid grid-cols-3 gap-3" data-id="element-10123" data-mp="2rciy8vtki4kg">
          <div data-id="element-10124" data-mp="2dhyol5bdutfk">
            <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10125" data-mp="2dii8grklfrw8">
              Genre
            </label>
            <select value={selectedGenre} onChange={e => setSelectedGenre(e.target.value)} className={`w-full text-sm rounded-md px-3 py-1.5 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10126" data-mp="23jsp0ud1tzdv">
              <option value="" data-id="element-10127" data-mp="1zk1tyeghox3j">All Genres</option>
              {genres.map(genre => <option key={genre} value={genre} data-id="element-10128" data-mp="3oz879pmothft">
                  {genre}
                </option>)}
            </select>
          </div>
          <div data-id="element-10129" data-mp="3rimrpkuzb0mg">
            <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10130" data-mp="3rj4l89q9b0qo">
              Subgenre
            </label>
            <select value={selectedSubgenre} onChange={e => setSelectedSubgenre(e.target.value)} className={`w-full text-sm rounded-md px-3 py-1.5 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10131" data-mp="3ihx1cwohm2g4">
              <option value="" data-id="element-10132" data-mp="a1legsds4wdf">All Subgenres</option>
              {subgenres.map(s => <option key={s} value={s} data-id="element-10133" data-mp="5b52zdtirktp">
                  {s}
                </option>)}
            </select>
          </div>
          <div data-id="element-10134" data-mp="bio6zy1lwis">
            <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10135" data-mp="c1vlmq2h2ik">
              Relationship Type
            </label>
            <select value={selectedRelationship} onChange={e => setSelectedRelationship(e.target.value)} className={`w-full text-sm rounded-md px-3 py-1.5 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} data-id="element-10136" data-mp="szeenmx0qs74">
              <option value="" data-id="element-10137" data-mp="1aco58ge2zml9">All Relationships</option>
              {relationshipTypes.map(type => <option key={type} value={type} data-id="element-10138" data-mp="1jazry7btu08f">
                  {type}
                </option>)}
            </select>
          </div>
        </div>

        {/* Desktop view controls */}
        <div className="hidden md:flex flex-wrap items-center justify-between gap-3" data-id="element-10139" data-mp="1t0t171ohy39i">
          <div className={`flex items-center space-x-1 border rounded-md p-1 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`} data-id="element-10140" data-mp="1t35irdqcebm1">
            <button onClick={() => setViewMode('cover')} className={`p-1.5 rounded ${viewMode === 'cover' ? `${darkMode ? 'bg-gray-600' : 'bg-white'} text-indigo-500` : ''}`} aria-label="Cover view" data-id="element-10141" data-mp="230tqr5cylfm5">
              <BookIcon className={`h-4 w-4 ${viewMode === 'cover' ? 'text-indigo-500' : darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10142" data-mp="2zqvr64l7d4ax" />
            </button>
            <button onClick={() => setViewMode('spine')} className={`p-1.5 rounded ${viewMode === 'spine' ? `${darkMode ? 'bg-gray-600' : 'bg-white'} text-indigo-500` : ''}`} aria-label="Spine view" data-id="element-10143" data-mp="39gfa1a8x0y5s">
              <LibraryIcon className={`h-4 w-4 ${viewMode === 'spine' ? 'text-indigo-500' : darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10144" data-mp="2gtcgyxn0dfmx" />
            </button>
          </div>

          {activeTab === 'ownedShelf' && <div className="relative" ref={viewOptionsRef} data-id="element-10145" data-mp="a08zlui9dgo4">
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center px-3 py-2 rounded-md ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-10146" data-mp="a2v5lppi3o48">
                <SlidersHorizontalIcon className="h-4 w-4 mr-2" data-id="element-10147" data-mp="hlw2hs5sqjim" />
                View Options
                <ChevronDownIcon className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} data-id="element-10148" data-mp="3mcvj2oq1e7w" />
              </button>
              {showFilters && <div className={`absolute right-0 top-full mt-2 w-64 rounded-md shadow-lg z-20 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-10149" data-mp="wa8z8rcpk6e6">
                  <div className="p-4 space-y-4" data-id="element-10150" data-mp="mhtghrtl7ds8">
                    <div data-id="element-10151" data-mp="rhpe99boy8zc">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10152" data-mp="rgwg2wqssrst">
                        Sort by
                      </label>
                      <select value={sortOption} onChange={e => setSortOption(e.target.value)} className={`w-full rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-10153" data-mp="1tus3uw4ywaxo">
                        <option value="seriesOrder" data-id="element-10154" data-mp="1fw0qxd1ucvb4">Series Order</option>
                        <option value="alphabetical" data-id="element-10155" data-mp="1aw64pmt0gtwe">Alphabetical</option>
                        <option value="dateOwned" data-id="element-10156" data-mp="1ay93owc55xd9">Date Owned</option>
                      </select>
                    </div>
                    <div data-id="element-10157" data-mp="116ctimws6gso">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10158" data-mp="115l9888mw5hp">
                        View Mode
                      </label>
                      <select value={seriesFilter} onChange={e => setSeriesFilter(e.target.value)} className={`w-full rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-10159" data-mp="s4vd13fgod3g">
                        <option value="all" data-id="element-10160" data-mp="37ultcwr9zn0d">All Series</option>
                        <option value="allBooks" data-id="element-10161" data-mp="37v4yfcvtugwz">All Books Owned</option>
                        <option value="completed" data-id="element-10162" data-mp="3ctybx5b467x5">Completed Series</option>
                        <option value="ongoing" data-id="element-10163" data-mp="2g3pgvx94nkdm">Ongoing Series</option>
                        <option value="standalone" data-id="element-10164" data-mp="2g1cqqwt7u3ph">
                          Standalone Books Only
                        </option>
                      </select>
                    </div>
                    <div data-id="element-10165" data-mp="2p2t0d2c7i1sn">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10166" data-mp="2tuu94ea61adx">
                        Format
                      </label>
                      <select value={selectedFormat} onChange={e => setSelectedFormat(e.target.value)} className={`w-full rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-10167" data-mp="21drlaw8vbsee">
                        <option value="" data-id="element-10168" data-mp="13tgjwjq7tymk">All Formats</option>
                        {formats.map(format => <option key={format} value={format} data-id="element-10169" data-mp="zseneanoo0oa">
                            {format.charAt(0).toUpperCase() + format.slice(1)}
                          </option>)}
                      </select>
                    </div>
                  </div>
                </div>}
            </div>}

          <button onClick={toggleEditMode} className={`px-3 py-2 rounded-md text-sm font-medium ${isEditMode ? darkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-10170" data-mp="1igjjqx20gjd2">
            {isEditMode ? 'Exit Edit Mode' : 'Edit Books'}
          </button>
        </div>

        {/* Bulk Actions in Edit Mode */}
        {isEditMode && selectedBooks.length > 0 && <div className={`mt-1 p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-10171" data-mp="221tiauzs3a6b">
            <div className="flex items-center justify-between mb-2" data-id="element-10172" data-mp="262dftzo9kfsz">
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10173" data-mp="1sbqs12k1kabo">
                {selectedBooks.length} book
                {selectedBooks.length !== 1 ? 's' : ''} selected
              </span>
              <button onClick={clearSelection} className={`text-xs ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-10174" data-mp="1ndozzs4n10o2">
                Clear selection
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-10175" data-mp="1ef3hfwz61lna">
              <select onChange={e => {
            const value = e.target.value;
            if (value) {
              bulkChangeStatus(value);
              e.target.value = '';
            }
          }} className={`text-sm rounded-md px-3 py-1.5 ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border border-gray-300 text-gray-900'}`} data-id="element-10176" data-mp="1cjufor17dgdh">
                <option value="" data-id="element-10177" data-mp="1wa7nuhdx6btm">Move to shelf...</option>
                {tabs.filter(tab => tab.id !== 'ownedShelf' && tab.id !== activeTab).map(tab => <option key={tab.id} value={tab.id} data-id="element-10178" data-mp="1rguyg51f20zt">
                      {tab.label}
                    </option>)}
              </select>
              <button onClick={bulkDelete} className={`px-3 py-1.5 rounded-md text-sm ${darkMode ? 'bg-red-900 text-white hover:bg-red-800' : 'bg-red-100 text-red-800 hover:bg-red-200'}`} data-id="element-10179" data-mp="2f4w518f1b7cl">
                Delete Selected
              </button>
              <button onClick={selectAllBooks} className={`px-3 py-1.5 rounded-md text-sm ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`} data-id="element-10180" data-mp="2pann3x6b9o5e">
                Select All
              </button>
            </div>
          </div>}
      </div>
    </div>;
};